import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestTimeRemaining, setGuestTimeRemaining] = useState(null);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const demoMode = localStorage.getItem('isDemo') === 'true';
    
    // Validate that storedUser is valid JSON before parsing
    if (token && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser && typeof parsedUser === 'object') {
          setUser(parsedUser);
          setIsDemo(demoMode);
          api.get('/auth/me')
            .then(res => {
              setUser(res.data);
              localStorage.setItem('user', JSON.stringify(res.data));
            })
            .catch(() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('isDemo');
              setUser(null);
              setIsDemo(false);
            })
            .finally(() => setLoading(false));
        } else {
          // Invalid user data, clean up
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('isDemo');
          setLoading(false);
        }
      } catch (e) {
        // JSON parse error, clean up invalid data
        console.error('Error parsing stored user:', e);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('isDemo');
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  // Guest session timer (30 minutes)
  useEffect(() => {
    if (user?.role === 'guest') {
      const loginTime = localStorage.getItem('guestLoginTime');
      if (!loginTime) {
        localStorage.setItem('guestLoginTime', Date.now().toString());
      }
      
      const interval = setInterval(() => {
        const startTime = parseInt(localStorage.getItem('guestLoginTime') || Date.now());
        const elapsed = Date.now() - startTime;
        const remaining = 30 * 60 * 1000 - elapsed; // 30 minutes in ms
        
        if (remaining <= 0) {
          logout();
          alert('انتهت جلسة العميل (30 دقيقة). يرجى تسجيل الدخول مرة أخرى.');
        } else {
          setGuestTimeRemaining(Math.floor(remaining / 1000 / 60)); // minutes remaining
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [user]);

  const login = async (email, password) => {
    // Get client timezone offset in minutes
    const clientTimezoneOffset = new Date().getTimezoneOffset();
    
    const response = await api.post('/auth/login', { 
      email, 
      password,
      client_timezone_offset: clientTimezoneOffset
    });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Check if demo user
    const isDemoUser = email === 'test' && password === 'test';
    localStorage.setItem('isDemo', isDemoUser.toString());
    setIsDemo(isDemoUser);
    
    // Reset guest timer on login
    if (userData.role === 'guest') {
      localStorage.setItem('guestLoginTime', Date.now().toString());
    }
    
    setUser(userData);
    
    return userData;
  };

  const logout = useCallback(async () => {
    // Call logout API to clear demo session on server
    try {
      await api.post('/auth/logout');
    } catch (e) {
      // Ignore errors on logout
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('guestLoginTime');
    localStorage.removeItem('isDemo');
    setUser(null);
    setIsDemo(false);
    setGuestTimeRemaining(null);
  }, []);

  const isAdmin = () => user?.role === 'admin';
  const isBarber = () => user?.role === 'barber';
  const isGuest = () => user?.role === 'guest';
  
  // Permission helpers
  const canCreate = () => user?.role === 'admin';
  const canEdit = () => ['admin', 'barber'].includes(user?.role);
  const canDelete = () => user?.role === 'admin';
  const canViewOnly = () => user?.role === 'guest';

  // Get token from localStorage
  const getToken = useCallback(() => {
    return localStorage.getItem('token');
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      isAdmin, 
      isBarber, 
      isGuest,
      canCreate,
      canEdit,
      canDelete,
      canViewOnly,
      guestTimeRemaining,
      isDemo,
      token: localStorage.getItem('token'),
      getToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};